import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import type {
  RemoteLaunchIntent,
  RemoteHostOwnerWrite,
  RemoteHostRead,
  RemoteInventoryIssueRead,
  RemoteSessionRead,
  RemoteSessionStatus,
} from '../../../api/generated/model'
import { fetchMe } from '../../../lib/auth'
import { USE_REAL_API } from '../../../lib/api'
import { useAuthStore } from '../../../store/auth'
import {
  approveRemoteHost,
  closeKnownRemoteSession,
  closeOwnedRemoteSession,
  listRemoteHostInventory,
  listRemoteSellerOrgs,
  readRemoteSession,
  reconcileRemoteAccess,
  requestRemoteLaunch,
  setRemoteHostOwner,
  toRemoteAccessSafeError,
} from './remote-access-api'
import type { RemoteAccessSafeError } from './remote-access-api'
import {
  closePreparedRemoteTab,
  postLaunchTicket,
  prepareRemoteTab,
  showRemoteTabFailure,
} from './remote-launch-handoff'
import type { PreparedRemoteTab } from './remote-launch-handoff'
import { isSessionPollingStatus, isSessionTerminalStatus } from './server-view-model'

const REMOTE_ACCESS_ROOT_QUERY_KEY = ['admin', 'remote-access'] as const
export const REMOTE_ACCESS_HOSTS_QUERY_KEY = [...REMOTE_ACCESS_ROOT_QUERY_KEY, 'hosts'] as const
const REMOTE_ACCESS_ME_QUERY_KEY = ['auth', 'me', 'remote-access'] as const
const SESSION_POLL_INTERVAL_MS = 2_000
const HOST_POLL_INTERVAL_MS = 15_000
const EMPTY_KNOWN_SESSION_IDS: ReadonlySet<string> = new Set<string>()

const POPUP_BLOCKED_ERROR: RemoteAccessSafeError = {
  status: null,
  code: 'remote_access_unavailable',
  correlationId: null,
}
const FORBIDDEN_ERROR: RemoteAccessSafeError = {
  status: 403,
  code: 'remote_access_forbidden',
  correlationId: null,
}
const INVALID_REASON_ERROR: RemoteAccessSafeError = {
  status: null,
  code: 'remote_access_unavailable',
  correlationId: null,
}

const DEFAULT_TAB_RECOVERY = {
  reauthentication: 'Bağlanmak için yeniden giriş yap.',
  generic: 'Bağlantı hazırlanamadı. PEXIFY Admin sekmesine geri dön.',
}

type RemoteAccessAuthState = {
  token: string | null
  epoch: number
  confirmedActorKey: string | null
}

const authEpochByClient = new WeakMap<QueryClient, RemoteAccessAuthState>()
const clearedAuthEpochByClient = new WeakMap<QueryClient, number>()

function remoteAccessAuthState(
  queryClient: QueryClient,
  token: string | null,
): RemoteAccessAuthState {
  const current = authEpochByClient.get(queryClient)
  if (!current) {
    const initial = { token, epoch: 0, confirmedActorKey: null }
    authEpochByClient.set(queryClient, initial)
    clearedAuthEpochByClient.set(queryClient, 0)
    return initial
  }
  if (current.token === token) return current
  const next = {
    token,
    epoch: current.epoch + 1,
    confirmedActorKey: token ? current.confirmedActorKey : null,
  }
  authEpochByClient.set(queryClient, next)
  return next
}

export type RemoteAccessAvailability = {
  state: 'loading' | 'available' | 'unavailable'
  canView: boolean
  canConnect: boolean
  canManage: boolean
  canAssignStores: boolean
  canDiscoverMembers: boolean
  canUseAttendedRollback: boolean
  hosts: RemoteHostRead[]
  inventoryIssues: RemoteInventoryIssueRead[]
  inventoryObservedAt: string | null
  isRefreshing: boolean
  error: RemoteAccessSafeError | null
  actorKey: string | null
}

export type RemoteLaunchFailureKind =
  | 'popup_blocked'
  | 'reauthentication_required'
  | 'forbidden'
  | 'launch_failed'
  | 'handoff_failed'
  | 'polling_failed'

export type RemoteHostLaunchController = {
  connect(): Promise<void>
  sessionId: string | null
  sessionStatus: RemoteSessionStatus | null
  correlationId: string | null
  isPending: boolean
  safeError: RemoteAccessSafeError | null
  failureKind: RemoteLaunchFailureKind | null
  clearTerminalAttempt(): void
}

export type RemoteAccessLaunchManager = {
  acceptTakeoverLaunch(hostId: string, intent: RemoteLaunchIntent): void
  launchByHost: Readonly<Record<string, RemoteHostLaunchController>>
  connect(hostId: string): Promise<void>
  clearTerminalAttempt(hostId: string): void
  applySessionUpdate(session: RemoteSessionRead): boolean
  knownSessionIds: ReadonlySet<string>
  closeableSessionByHost: Readonly<Record<string, RemoteSessionRead>>
  closeableSessionIds: ReadonlySet<string>
  applyCloseableSessionUpdate(session: RemoteSessionRead): boolean
}

type RemoteAccessLaunchManagerOptions = {
  actorKey?: string | null
  hosts?: readonly RemoteHostRead[]
  tabRecovery?: {
    reauthentication: string
    generic: string
  }
}

export type RemoteAccessManagement = {
  reconcile(reason: string): Promise<number>
  closeKnownSession(sessionId: string, reason: string): Promise<RemoteSessionRead>
  closeOwnedSession(sessionId: string, reason: string): Promise<RemoteSessionRead>
  approveHost(hostId: string): Promise<RemoteHostRead>
  setHostOwner(hostId: string, owner: RemoteHostOwnerWrite): Promise<RemoteHostRead>
  isPending: boolean
  safeError: RemoteAccessSafeError | null
}

type LaunchAttempt = {
  key: string
  tab: PreparedRemoteTab
}

type HostLaunchState = {
  sessionId: string | null
  sessionStatus: RemoteSessionStatus | null
  correlationId: string | null
  mutationPending: boolean
  hasAttempt: boolean
  safeError: RemoteAccessSafeError | null
  failureKind: RemoteLaunchFailureKind | null
}

const EMPTY_HOST_LAUNCH_STATE: HostLaunchState = {
  sessionId: null,
  sessionStatus: null,
  correlationId: null,
  mutationPending: false,
  hasAttempt: false,
  safeError: null,
  failureKind: null,
}

function isActiveAdminMembership(orgType: string | null | undefined): boolean {
  return Boolean(orgType?.startsWith('A'))
}

function shouldSkipRetry(error: unknown): boolean {
  const safe = toRemoteAccessSafeError(error)
  if (safe.status === 403 || safe.status === 404 || safe.status === 409) return true
  return typeof error === 'object'
    && error !== null
    && 'code' in error
    && error.code === 'status_unknown'
}

function remoteAccessRetry(failureCount: number, error: unknown): boolean {
  return !shouldSkipRetry(error) && failureCount < 2
}

function shouldSkipHostRetry(error: unknown): boolean {
  return toRemoteAccessSafeError(error).status === 401 || shouldSkipRetry(error)
}

function updateHostState(
  hostId: string,
  patch: Partial<HostLaunchState>,
  update: React.Dispatch<React.SetStateAction<Record<string, HostLaunchState>>>,
): void {
  update((current) => ({
    ...current,
    [hostId]: { ...(current[hostId] ?? EMPTY_HOST_LAUNCH_STATE), ...patch },
  }))
}

function validatedReason(reason: string): string {
  const trimmed = reason.trim()
  if (trimmed.length === 0 || trimmed.length > 500) throw INVALID_REASON_ERROR
  return trimmed
}

function useRemoteAccessAvailabilityQuery(): RemoteAccessAvailability {
  const queryClient = useQueryClient()
  const accessToken = useAuthStore((state) => state.accessToken)
  const authState = remoteAccessAuthState(queryClient, accessToken)
  const authEpoch = authState.epoch

  useEffect(() => {
    if (clearedAuthEpochByClient.get(queryClient) === authEpoch) return
    queryClient.removeQueries({ queryKey: REMOTE_ACCESS_ROOT_QUERY_KEY })
    queryClient.removeQueries({
      queryKey: REMOTE_ACCESS_ME_QUERY_KEY,
      predicate: (query) => query.queryKey[3] !== authEpoch,
    })
    clearedAuthEpochByClient.set(queryClient, authEpoch)
  }, [authEpoch, queryClient])

  const meQuery = useQuery({
    queryKey: [...REMOTE_ACCESS_ME_QUERY_KEY, authEpoch],
    queryFn: fetchMe,
    enabled: USE_REAL_API && Boolean(accessToken),
    retry: false,
  })
  const me = meQuery.data
  const capabilities = me?.capabilities ?? []
  const hasAdminMembership = Boolean(
    me?.user.is_active
      && me.active_membership
      && isActiveAdminMembership(me.active_membership.org_type),
  )
  const hasViewCapability = capabilities.includes('remote_access.view')
  const actorKey = hasAdminMembership && me?.active_membership
    ? `${me.user.id}:${me.active_membership.id}`
    : null
  if (meQuery.isSuccess && authState.token === accessToken) {
    authState.confirmedActorKey = actorKey
  }
  const launchActorKey = !accessToken
    ? null
    : meQuery.isSuccess
      ? actorKey
      : authState.confirmedActorKey
  const hostQueryEnabled = USE_REAL_API
    && Boolean(accessToken)
    && Boolean(actorKey)
    && hasViewCapability
  const hostQuery = useQuery({
    queryKey: [...REMOTE_ACCESS_HOSTS_QUERY_KEY, actorKey],
    queryFn: listRemoteHostInventory,
    enabled: hostQueryEnabled,
    staleTime: HOST_POLL_INTERVAL_MS,
    retry: (failureCount, error) => !shouldSkipHostRetry(error) && failureCount < 2,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    refetchInterval: (query) => (
      query.state.error && shouldSkipHostRetry(query.state.error)
        ? false
        : HOST_POLL_INTERVAL_MS
    ),
  })

  const available = hostQueryEnabled && hostQuery.isSuccess
  const loading = USE_REAL_API
    && Boolean(accessToken)
    && (meQuery.isPending || (hostQueryEnabled && hostQuery.isPending))
  const error = hostQuery.error ? toRemoteAccessSafeError(hostQuery.error) : null
  const canUseAttendedRollback = Boolean(
    hasAdminMembership
      && hasViewCapability
      && error?.status === 404
      && error.code === 'remote_access_not_available',
  )

  return {
    state: loading ? 'loading' : available ? 'available' : 'unavailable',
    canView: available,
    canConnect: available && capabilities.includes('remote_access.connect'),
    canManage: available && capabilities.includes('remote_access.manage'),
    // Naming the stores on a machine is its own authority, so the operations
    // team can keep that record without being able to grant server access.
    canAssignStores: available && capabilities.includes('remote_access.store_link'),
    canDiscoverMembers: available && capabilities.includes('platform.user_admin'),
    canUseAttendedRollback,
    hosts: available ? (hostQuery.data?.hosts ?? []) : [],
    inventoryIssues: available ? (hostQuery.data?.inventoryIssues ?? []) : [],
    inventoryObservedAt: available ? (hostQuery.data?.inventoryObservedAt ?? null) : null,
    isRefreshing: available && hostQuery.isFetching && !hostQuery.isPending,
    error,
    // Keep the confirmed actor identity through a same-user credential refresh
    // so in-flight launch/session state survives. Host authority remains withheld
    // until the fresh /me + assigned-host requests succeed above. Explicit logout
    // clears immediately; a confirmed actor change resets the launch manager.
    actorKey: launchActorKey,
  }
}

export function useRemoteAccessAvailability(): RemoteAccessAvailability {
  return useRemoteAccessAvailabilityQuery()
}

export function useRemoteAccessLaunchManager(
  hostIds: readonly string[],
  options: RemoteAccessLaunchManagerOptions = {},
): RemoteAccessLaunchManager {
  const queryClient = useQueryClient()
  const actorKey = options.actorKey === undefined ? 'standalone' : options.actorKey
  const tabRecovery = options.tabRecovery ?? DEFAULT_TAB_RECOVERY
  const stableHostIds = useMemo(
    () => [...new Set(hostIds)].sort(),
    [hostIds],
  )
  const allowedHostIds = useMemo(() => new Set(stableHostIds), [stableHostIds])
  const allowedHostIdsRef = useRef(allowedHostIds)
  const attemptsRef = useRef(new Map<string, LaunchAttempt | null>())
  const retiredSessionIdsRef = useRef(new Set<string>())
  const refreshedTerminalSessionsRef = useRef(new Set<string>())
  const actorKeyRef = useRef(actorKey)
  const tabRecoveryRef = useRef(tabRecovery)
  const mountedRef = useRef(true)
  const [stateActorKey, setStateActorKey] = useState(actorKey)
  const [stateByHost, setStateByHost] = useState<Record<string, HostLaunchState>>({})
  const [closeableSessionUpdates, setCloseableSessionUpdates] = useState<Record<string, RemoteSessionRead>>({})

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    allowedHostIdsRef.current = allowedHostIds
  }, [allowedHostIds])

  useEffect(() => {
    tabRecoveryRef.current = tabRecovery
  }, [tabRecovery])

  useEffect(() => {
    if (actorKeyRef.current === actorKey) return
    for (const attempt of attemptsRef.current.values()) {
      if (attempt) closePreparedRemoteTab(attempt.tab)
    }
    attemptsRef.current.clear()
    retiredSessionIdsRef.current.clear()
    refreshedTerminalSessionsRef.current.clear()
    actorKeyRef.current = actorKey
    setStateActorKey(actorKey)
    setStateByHost({})
    setCloseableSessionUpdates({})
  }, [actorKey])

  const actorStateIsCurrent = stateActorKey === actorKey
  const closeableSessionByHost = useMemo(() => {
    const sessions: Record<string, RemoteSessionRead> = {}
    if (!actorKey || !actorStateIsCurrent) return sessions
    for (const host of options.hosts ?? []) {
      const session = host.closeable_session
      if (
        !session
        || session.host_id !== host.id
        || !allowedHostIds.has(host.id)
        || !isSessionPollingStatus(session.status)
        || host.current_session
        || stateByHost[host.id]?.hasAttempt
        || closeableSessionUpdates[session.id]?.status === 'closed'
      ) continue
      sessions[host.id] = closeableSessionUpdates[session.id] ?? session
    }
    return sessions
  }, [actorKey, actorStateIsCurrent, allowedHostIds, options.hosts, stateByHost, closeableSessionUpdates])
  const closeableSessionIds = new Set(Object.values(closeableSessionByHost)
    .filter((session) => session.status === 'active')
    .map((session) => session.id))

  const applyCloseableSessionUpdate = (session: RemoteSessionRead): boolean => {
    if (
      !mountedRef.current
      || actorKeyRef.current !== actorKey
      || closeableSessionByHost[session.host_id]?.id !== session.id
      || (session.status !== 'closing' && session.status !== 'closed')
    ) return false
    if (session.status === 'closed') retiredSessionIdsRef.current.add(session.id)
    setCloseableSessionUpdates((current) => ({ ...current, [session.id]: session }))
    return true
  }

  useEffect(() => {
    if (!actorKey || !actorStateIsCurrent) return
    for (const host of options.hosts ?? []) {
      const session = host.current_session
      if (
        !session
        || session.host_id !== host.id
        || !allowedHostIds.has(host.id)
        || !isSessionPollingStatus(session.status)
        || attemptsRef.current.has(host.id)
        || retiredSessionIdsRef.current.has(session.id)
      ) continue
      // A recovered lease blocks another Connect without creating a browser tab.
      attemptsRef.current.set(host.id, null)
      updateHostState(host.id, {
        ...EMPTY_HOST_LAUNCH_STATE,
        sessionId: session.id,
        sessionStatus: session.status,
        correlationId: session.correlation_id,
        hasAttempt: true,
      }, setStateByHost)
    }
  }, [actorKey, actorStateIsCurrent, allowedHostIds, options.hosts])

  const sessionQueries = useQueries({
    queries: stableHostIds.map((hostId) => {
      const launchState = actorStateIsCurrent ? stateByHost[hostId] : undefined
      const sessionId = launchState?.sessionId ?? null
      const sessionStatus = launchState?.sessionStatus ?? null
      const sessionQueryKey = sessionId ?? `unlaunched:${hostId}`
      return {
        queryKey: [
          'admin',
          'remote-access',
          'sessions',
          actorKey ?? 'no-actor',
          sessionQueryKey,
        ] as const,
        queryFn: () => readRemoteSession(sessionId as string),
        enabled: Boolean(sessionId && sessionStatus && isSessionPollingStatus(sessionStatus)),
        retry: remoteAccessRetry,
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchInterval: (query: { state: { data?: RemoteSessionRead; error: unknown } }) => {
          if (query.state.error && shouldSkipRetry(query.state.error)) return false
          const latestStatus = query.state.data?.status ?? sessionStatus
          return latestStatus && isSessionPollingStatus(latestStatus)
            ? SESSION_POLL_INTERVAL_MS
            : false
        },
      }
    }),
  })

  const connect = useCallback(async (hostId: string): Promise<void> => {
    if (
      !actorKey
      || actorKeyRef.current !== actorKey
      || !allowedHostIdsRef.current.has(hostId)
      || attemptsRef.current.has(hostId)
      || closeableSessionByHost[hostId]
    ) return

    const tab = prepareRemoteTab()
    if (!tab) {
      if (mountedRef.current && allowedHostIdsRef.current.has(hostId)) {
        updateHostState(hostId, {
          safeError: POPUP_BLOCKED_ERROR,
          failureKind: 'popup_blocked',
        }, setStateByHost)
      }
      return
    }

    const key = crypto.randomUUID()
    attemptsRef.current.set(hostId, { key, tab })
    if (mountedRef.current && allowedHostIdsRef.current.has(hostId)) {
      updateHostState(hostId, {
        mutationPending: true,
        hasAttempt: true,
        safeError: null,
        failureKind: null,
      }, setStateByHost)
    }

    try {
      const outcome = await requestRemoteLaunch(hostId, key)
      if (!mountedRef.current) return

      if (outcome.kind === 'replayed') {
        closePreparedRemoteTab(tab)
        updateHostState(hostId, {
          sessionId: outcome.sessionId,
          sessionStatus: outcome.status,
          correlationId: outcome.correlationId,
          mutationPending: false,
          hasAttempt: true,
          safeError: null,
          failureKind: null,
        }, setStateByHost)
        return
      }

      const { session_id, status, correlation_id } = outcome.intent
      updateHostState(hostId, {
        sessionId: session_id,
        sessionStatus: status,
        correlationId: correlation_id,
        mutationPending: false,
        hasAttempt: true,
        safeError: null,
        failureKind: null,
      }, setStateByHost)
      try {
        postLaunchTicket(tab, outcome.intent)
      } catch {
        updateHostState(hostId, {
          safeError: POPUP_BLOCKED_ERROR,
          failureKind: 'handoff_failed',
        }, setStateByHost)
        showRemoteTabFailure(tab, tabRecoveryRef.current.generic)
      }
    } catch (error) {
      if (!mountedRef.current) return
      const safeError = toRemoteAccessSafeError(error)
      const terminalPreDispatch = safeError.code === 'remote_access_reauthentication_required'
        || safeError.status === 403
        || safeError.status === 404
      if (terminalPreDispatch) attemptsRef.current.delete(hostId)
      updateHostState(hostId, {
        mutationPending: false,
        hasAttempt: !terminalPreDispatch,
        safeError,
        failureKind: safeError.code === 'remote_access_reauthentication_required'
          ? 'reauthentication_required'
          : safeError.code === 'remote_access_forbidden'
            ? 'forbidden'
            : 'launch_failed',
      }, setStateByHost)
      showRemoteTabFailure(
        tab,
        safeError.code === 'remote_access_reauthentication_required'
          ? tabRecoveryRef.current.reauthentication
          : tabRecoveryRef.current.generic,
      )
    }
  }, [actorKey, closeableSessionByHost])

  const effectiveSessionByHost = Object.fromEntries(stableHostIds.map((hostId, index) => {
    const local = actorStateIsCurrent
      ? (stateByHost[hostId] ?? EMPTY_HOST_LAUNCH_STATE)
      : EMPTY_HOST_LAUNCH_STATE
    const remoteSession = sessionQueries[index]?.data
    return [hostId, {
      id: remoteSession?.id ?? local.sessionId,
      status: remoteSession?.status ?? local.sessionStatus,
      correlationId: remoteSession?.correlation_id ?? local.correlationId,
    }]
  }))

  const terminalSessionIds = Object.values(effectiveSessionByHost)
    .filter((session) => session.id && session.status && isSessionTerminalStatus(session.status))
    .map((session) => session.id)
    .join(',')

  useEffect(() => {
    if (!terminalSessionIds) return
    const unseen = terminalSessionIds.split(',').filter((id) => !refreshedTerminalSessionsRef.current.has(id))
    if (unseen.length === 0) return
    unseen.forEach((id) => refreshedTerminalSessionsRef.current.add(id))
    void queryClient.invalidateQueries({ queryKey: REMOTE_ACCESS_HOSTS_QUERY_KEY })
  }, [terminalSessionIds, queryClient])

  const clearTerminalAttempt = (hostId: string): void => {
    const status = effectiveSessionByHost[hostId]?.status
    if (status !== 'closed' && status !== 'failed') return
    const sessionId = effectiveSessionByHost[hostId]?.id
    if (sessionId) retiredSessionIdsRef.current.add(sessionId)
    attemptsRef.current.delete(hostId)
    if (mountedRef.current && allowedHostIdsRef.current.has(hostId)) {
      setStateByHost((current) => {
        const next = { ...current }
        delete next[hostId]
        return next
      })
    }
  }

  const applySessionUpdate = useCallback((session: RemoteSessionRead): boolean => {
    const matchingHost = Object.entries(stateByHost).find(([, state]) => (
      state.sessionId === session.id
    ))
    if (!matchingHost || matchingHost[0] !== session.host_id) return false
    const [hostId] = matchingHost
    queryClient.setQueryData(
      [
        'admin',
        'remote-access',
        'sessions',
        actorKey ?? 'no-actor',
        session.id,
      ],
      session,
    )
    updateHostState(hostId, {
      sessionStatus: session.status,
      correlationId: session.correlation_id,
      mutationPending: false,
      hasAttempt: true,
      safeError: null,
      failureKind: null,
    }, setStateByHost)
    return true
  }, [actorKey, queryClient, stateByHost])

  const launchByHost = Object.fromEntries(
    stableHostIds.map((hostId, index) => {
      const local = actorStateIsCurrent
        ? (stateByHost[hostId] ?? EMPTY_HOST_LAUNCH_STATE)
        : EMPTY_HOST_LAUNCH_STATE
      const sessionQuery = sessionQueries[index]
      const effectiveSession = effectiveSessionByHost[hostId]
      const sessionStatus = effectiveSession?.status ?? null
      const sessionId = effectiveSession?.id ?? null
      const correlationId = effectiveSession?.correlationId ?? null
      const unresolved = sessionStatus ? sessionStatus !== 'active' && isSessionPollingStatus(sessionStatus) : false
      const safeError = sessionQuery?.error
        ? toRemoteAccessSafeError(sessionQuery.error)
        : local.safeError
      const controller: RemoteHostLaunchController = {
        connect: () => connect(hostId),
        sessionId,
        sessionStatus,
        correlationId,
        isPending: local.mutationPending || (local.hasAttempt && (!sessionStatus || unresolved)),
        safeError,
        failureKind: sessionQuery?.error ? 'polling_failed' : local.failureKind,
        clearTerminalAttempt: () => clearTerminalAttempt(hostId),
      }
      return [hostId, controller]
    }),
  )

  const knownSessionIds = new Set(
    Object.values(launchByHost)
      .map((controller) => controller.sessionId)
      .filter((sessionId): sessionId is string => sessionId !== null),
  )

  const acceptTakeoverLaunch = useCallback((hostId: string, intent: RemoteLaunchIntent) => {
    if (!mountedRef.current || !allowedHostIdsRef.current.has(hostId)) return
    attemptsRef.current.set(hostId, null)
    updateHostState(hostId, {
      ...EMPTY_HOST_LAUNCH_STATE, hasAttempt: true,
      sessionId: intent.session_id, sessionStatus: intent.status, correlationId: intent.correlation_id,
    }, setStateByHost)
  }, [])

  return {
    acceptTakeoverLaunch,
    launchByHost,
    connect,
    clearTerminalAttempt,
    applySessionUpdate,
    knownSessionIds,
    closeableSessionByHost,
    closeableSessionIds,
    applyCloseableSessionUpdate,
  }
}

export function useRemoteAccessManagement(
  canManage: boolean,
  knownSessionIds: ReadonlySet<string> = EMPTY_KNOWN_SESSION_IDS,
  closeableSessionIds: ReadonlySet<string> = EMPTY_KNOWN_SESSION_IDS,
  onCloseAccepted?: (session: RemoteSessionRead) => void,
): RemoteAccessManagement {
  const queryClient = useQueryClient()
  const [localError, setLocalError] = useState<RemoteAccessSafeError | null>(null)
  const reconcileMutation = useMutation({
    mutationFn: (reason: string) => reconcileRemoteAccess(reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: REMOTE_ACCESS_HOSTS_QUERY_KEY })
    },
  })
  const closeMutation = useMutation({
    mutationFn: ({ sessionId, reason }: { sessionId: string; reason: string; onAccepted?: (session: RemoteSessionRead) => void }) => (
      closeKnownRemoteSession(sessionId, reason)
    ),
    onSuccess: async (session, { onAccepted }) => {
      if (session.status === 'closing' || session.status === 'closed') onAccepted?.(session)
      await queryClient.invalidateQueries({ queryKey: REMOTE_ACCESS_HOSTS_QUERY_KEY })
    },
  })

  const closeOwnedMutation = useMutation({
    retry: false,
    mutationFn: ({ sessionId, reason }: { sessionId: string; reason: string; onAccepted?: (session: RemoteSessionRead) => void }) => (
      closeOwnedRemoteSession(sessionId, reason)
    ),
    onSuccess: async (session, { onAccepted }) => {
      if (session.status === 'closing' || session.status === 'closed') onAccepted?.(session)
      await queryClient.invalidateQueries({ queryKey: REMOTE_ACCESS_HOSTS_QUERY_KEY })
    },
  })

  const approveMutation = useMutation({
    mutationFn: (hostId: string) => approveRemoteHost(hostId),
    onSuccess: async () => {
      // The row stops being pending and joins the ordinary lifecycle, so the
      // list has to be re-read rather than patched in place.
      await queryClient.invalidateQueries({ queryKey: REMOTE_ACCESS_HOSTS_QUERY_KEY })
    },
  })

  const ownerMutation = useMutation({
    retry: false,
    mutationFn: ({ hostId, owner }: { hostId: string; owner: RemoteHostOwnerWrite }) => (
      setRemoteHostOwner(hostId, owner)
    ),
    onSuccess: async () => {
      // The row may move between the "ours" and "customers" sections.
      await queryClient.invalidateQueries({ queryKey: REMOTE_ACCESS_HOSTS_QUERY_KEY })
    },
  })

  const reconcile = useCallback(async (reason: string): Promise<number> => {
    if (!canManage) {
      setLocalError(FORBIDDEN_ERROR)
      throw FORBIDDEN_ERROR
    }
    let normalizedReason: string
    try {
      normalizedReason = validatedReason(reason)
    } catch (error) {
      const safeError = toRemoteAccessSafeError(error)
      setLocalError(safeError)
      throw safeError
    }
    setLocalError(null)
    const result = await reconcileMutation.mutateAsync(normalizedReason)
    return result.reconciled_hosts
  }, [canManage, reconcileMutation])

  const closeKnownSession = useCallback(async (
    sessionId: string,
    reason: string,
  ): Promise<RemoteSessionRead> => {
    if (!canManage || !knownSessionIds.has(sessionId)) {
      setLocalError(FORBIDDEN_ERROR)
      throw FORBIDDEN_ERROR
    }
    let normalizedReason: string
    try {
      normalizedReason = validatedReason(reason)
    } catch (error) {
      const safeError = toRemoteAccessSafeError(error)
      setLocalError(safeError)
      throw safeError
    }
    setLocalError(null)
    return closeMutation.mutateAsync({ sessionId, reason: normalizedReason, onAccepted: onCloseAccepted })
  }, [canManage, closeMutation, knownSessionIds, onCloseAccepted])

  const closeOwnedSession = useCallback(async (
    sessionId: string,
    reason: string,
  ): Promise<RemoteSessionRead> => {
    if (!canManage || !closeableSessionIds.has(sessionId)) {
      setLocalError(FORBIDDEN_ERROR)
      throw FORBIDDEN_ERROR
    }
    let normalizedReason: string
    try {
      normalizedReason = validatedReason(reason)
    } catch (error) {
      const safeError = toRemoteAccessSafeError(error)
      setLocalError(safeError)
      throw safeError
    }
    setLocalError(null)
    return closeOwnedMutation.mutateAsync({ sessionId, reason: normalizedReason, onAccepted: onCloseAccepted })
  }, [canManage, closeOwnedMutation, closeableSessionIds, onCloseAccepted])

  const approveHost = useCallback(async (hostId: string): Promise<RemoteHostRead> => {
    if (!canManage) {
      setLocalError(FORBIDDEN_ERROR)
      throw FORBIDDEN_ERROR
    }
    setLocalError(null)
    return approveMutation.mutateAsync(hostId)
  }, [approveMutation, canManage])

  const setHostOwner = useCallback(async (
    hostId: string,
    owner: RemoteHostOwnerWrite,
  ): Promise<RemoteHostRead> => {
    if (!canManage) {
      setLocalError(FORBIDDEN_ERROR)
      throw FORBIDDEN_ERROR
    }
    setLocalError(null)
    return ownerMutation.mutateAsync({ hostId, owner })
  }, [canManage, ownerMutation])

  const mutationError = reconcileMutation.error ?? closeMutation.error ?? closeOwnedMutation.error ?? approveMutation.error
  return {
    reconcile,
    closeKnownSession,
    closeOwnedSession,
    approveHost,
    setHostOwner,
    isPending: reconcileMutation.isPending || closeMutation.isPending || closeOwnedMutation.isPending || approveMutation.isPending || ownerMutation.isPending,
    safeError: localError ?? (mutationError ? toRemoteAccessSafeError(mutationError) : null),
  }
}

const REMOTE_SELLER_ORGS_QUERY_KEY = ['remote-access', 'seller-orgs'] as const

/** Sellers a customer machine can be given to; read only while the owner dialog is open. */
export function useRemoteSellerOrgs(enabled: boolean) {
  return useQuery({
    queryKey: REMOTE_SELLER_ORGS_QUERY_KEY,
    queryFn: ({ signal }) => listRemoteSellerOrgs(signal),
    enabled: USE_REAL_API && enabled,
    staleTime: 60_000,
    retry: false,
  })
}
